import { deriveMumHubStage, parseIsoDate } from "./stage";

/**
 * How long a Mum Hub purchase lasts (MAR-247).
 *
 * The website sells through Stripe rather than the App Store, so it has no
 * product type to worry about, but it sells the same timed access on the same
 * terms and its paywall has to state the same durations. This is the twin of
 * `mobile/src/lib/mum-hub/access.ts`; the two carry the same rule because it is
 * the Worker's rule, and neither can import the other.
 *
 * ## The Worker's rule, restated here and nowhere else in the app
 *
 * - One stage lasts {@link STAGE_ACCESS_MONTHS} months from the day it is
 *   bought. It does not matter which stage or when.
 * - The whole journey lasts {@link JOURNEY_ACCESS_MONTHS} months, less
 *   {@link MONTHS_PER_COMPLETED_TRIMESTER} for every WHOLE trimester she has
 *   already completed on the day she buys it. So twelve, nine, six or three.
 * - The upgrade from one stage to the journey gets the journey's allowance
 *   less the time already spent on the stage, and never less than
 *   {@link MINIMUM_UPGRADE_MONTHS} month.
 *
 * ## Why the app computes it at all, when the Worker is the authority
 *
 * For the LABEL and for nothing else. Nothing here grants anything, nothing
 * here hides anything, and no screen compares its own answer with the Worker's.
 * The end date she is shown after buying is `expiresAt` off the entitlements
 * read, which is the Worker's own figure. If the two ever disagree, the Worker
 * is right and this file is the thing to fix.
 *
 * ## Everything is null when an input is missing
 *
 * A journey duration needs her due date, and an upgrade duration also needs the
 * day she bought her stage. Either missing and the answer is null, which the
 * copy renders as "up to twelve months" rather than as a specific promise. A
 * guessed number here would be a term of sale invented by a client.
 */

/** Months of access one stage buys. */
export const STAGE_ACCESS_MONTHS = 4;

/** Months of access the whole journey buys, before any deduction. */
export const JOURNEY_ACCESS_MONTHS = 12;

/** Taken off the journey for each whole trimester already completed. */
export const MONTHS_PER_COMPLETED_TRIMESTER = 3;

/** The floor on an upgrade, however much of her stage she has used. */
export const MINIMUM_UPGRADE_MONTHS = 1;

/**
 * Whole trimesters completed on `now`, from the date on file.
 *
 * Zero through three, or null when the date cannot be read. The stage she is
 * standing in IS the count of trimesters behind her, which is why this is
 * derived from `deriveMumHubStage` rather than from a second piece of date
 * arithmetic that could disagree with the stage she is being shown.
 *
 * A date whose post-partum window has already ended counts as three: she is
 * past every trimester, which is exactly what the Worker sees too.
 */
export function completedTrimesters(dueDate: string, now: Date): number | null {
  const derived = deriveMumHubStage(dueDate, now);
  if (derived === null) return null;
  if (derived.kind === "window-ended") return 3;
  switch (derived.stage) {
    case "first-trimester":
      return 0;
    case "second-trimester":
      return 1;
    case "third-trimester":
      return 2;
    case "new-mum":
      return 3;
  }
}

/**
 * How many months the whole journey would give her if she bought it today, or
 * null when there is no readable due date to work it out from.
 */
export function journeyAccessMonths(
  dueDate: string | null,
  now: Date,
): number | null {
  if (!dueDate) return null;
  const completed = completedTrimesters(dueDate, now);
  if (completed === null) return null;
  const months =
    JOURNEY_ACCESS_MONTHS - completed * MONTHS_PER_COMPLETED_TRIMESTER;
  // Three completed trimesters leaves three months, and the arithmetic cannot
  // go below that. The clamp is here so a future change to either constant
  // cannot quietly print a zero or a negative number of months.
  return Math.max(MONTHS_PER_COMPLETED_TRIMESTER, months);
}

/**
 * Whole calendar months between an ISO timestamp and `now`, or null.
 *
 * Calendar months rather than days divided by thirty, because the thing being
 * counted is "four months of access" and a customer reads that as the same day
 * of the month four months on. A day of the month earlier than the start day
 * has not completed that month.
 */
export function monthsElapsed(since: string, now: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(since);
  if (!match) return null;
  if (parseIsoDate(`${match[1]}-${match[2]}-${match[3]}`) === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const months =
    (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month);
  const whole = now.getDate() < day ? months - 1 : months;
  // A date in the future has used nothing. Negative months would take months
  // off nothing and hand an upgrader a longer allowance than the journey.
  return whole < 0 ? 0 : whole;
}

/**
 * What the upgrade would give her today: the journey's allowance less the time
 * already spent on the stage she owns, floored at one month.
 *
 * Null when either half is unknown, on the rule at the top of this file.
 */
export function upgradeAccessMonths({
  journeyMonths,
  stagePurchasedAt,
  now,
}: {
  journeyMonths: number | null;
  stagePurchasedAt: string | null;
  now: Date;
}): number | null {
  if (journeyMonths === null || !stagePurchasedAt) return null;
  const used = monthsElapsed(stagePurchasedAt, now);
  if (used === null) return null;
  return Math.max(MINIMUM_UPGRADE_MONTHS, journeyMonths - used);
}
